import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Landmark, Scale, Plus, Minus, Trash2, Loader2, TrendingUp, TrendingDown, ArrowDownLeft } from "lucide-react";
import { api, BRL, formatDateBR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle } from "@/components/ui/dialog";
import MoneyInput from "@/components/MoneyInput";
import { CaixaCdiCard } from "@/components/CaixaCdiCard";
import { ReceiptField, ReceiptInline } from "@/components/ReceiptAttach";

const KIND = {
  entrada: { i: TrendingUp, c: "bg-emerald-50 text-emerald-700" },
  saida: { i: TrendingDown, c: "bg-rose-50 text-rose-600" },
  ajuste: { i: Scale, c: "bg-sky-50 text-sky-700" },
  rendimento: { i: TrendingUp, c: "bg-teal-50 text-teal-700" },
};
const FILTERS = [["", "Tudo"], ["entrada", "Entradas"], ["saida", "Saídas"], ["rendimento", "Rendimentos"], ["ajuste", "Ajustes"]];

const MovDialog = ({ mode, onClose, balance, onSaved }) => {
  const [amount, setAmount] = useState(0);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [receipt, setReceipt] = useState("");
  useEffect(() => { setAmount(0); setText(""); setReceipt(""); }, [mode]);
  const isAdj = mode === "ajuste";
  const diff = Math.round((Number(amount || 0) - balance) * 100) / 100;
  const submit = async () => {
    if (!isAdj && mode !== "transfer" && !text.trim()) return toast.error("Informe a descrição");
    if (isAdj && !text.trim()) return toast.error("Informe o motivo");
    setBusy(true);
    try {
      if (isAdj) await api.post("/finance/conta-dia/ajuste", { real_balance: Number(amount || 0), reason: text });
      else if (mode === "transfer") await api.post("/finance/conta-dia/transfer-from-caixa", { kind: "entrada", amount: Number(amount || 0), description: text });
      else await api.post("/finance/conta-dia/movs", { kind: mode, amount: Number(amount || 0), description: text, receipt_path: receipt });
      toast.success("Lançado"); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao salvar"); }
    finally { setBusy(false); }
  };
  const title = { ajuste: "Ajuste de saldo", entrada: "Nova entrada", saida: "Nova saída", transfer: "Trazer do Caixa" }[mode];
  return (
    <Dialog open={!!mode} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md w-[calc(100vw-1.5rem)] sm:w-full" data-testid="conta-dia-dialog">
        <DialogHeader><DialogTitle className="font-heading font-black text-slate-900">{title}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {mode === "transfer" && <p className="rounded-xl bg-sky-50 text-sky-800 p-2.5 text-xs">O valor sai do saldo do Caixa da empresa e entra nesta conta. Aparece nos dois extratos.</p>}
          {isAdj && <div className="rounded-xl bg-slate-50 p-3 flex justify-between text-sm"><span className="text-slate-500">Saldo no sistema</span><b className="tabular-nums">{BRL(balance)}</b></div>}
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">{isAdj ? "Saldo real no banco" : "Valor"}</label>
            <MoneyInput value={amount} onChange={setAmount} className="w-full" data-testid="conta-dia-amount" />
          </div>
          {isAdj && Number(amount) > 0 && (
            <p className={`rounded-xl p-2.5 text-sm font-bold flex justify-between ${diff >= 0 ? "bg-emerald-50 text-emerald-800" : "bg-rose-50 text-rose-800"}`} data-testid="conta-dia-diff">
              <span>Ajuste</span><span className="tabular-nums">{diff >= 0 ? "+ " : "− "}{BRL(Math.abs(diff))}</span>
            </p>
          )}
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">{isAdj ? "Motivo" : mode === "transfer" ? "Observação (opcional)" : "Descrição"}</label>
            <Input value={text} onChange={(e) => setText(e.target.value)} placeholder={isAdj ? "Ex.: conferência com extrato" : mode === "transfer" ? "Ex.: reforço para pagar o cartão" : "Ex.: tarifa bancária"} data-testid="conta-dia-text" />
          </div>
          {mode === "saida" && <ReceiptField value={receipt} onChange={setReceipt} testid="conta-dia-receipt" />}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={busy || (isAdj ? diff === 0 : !(Number(amount) > 0))} className="bg-slate-900 text-white" data-testid="conta-dia-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salvar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ r, i, onDelete, onChanged }) => {
  const k = KIND[r.kind] || KIND.ajuste;
  return (
    <li className="py-2.5 flex items-start gap-3" data-testid={`conta-dia-row-${i}`}>
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${k.c}`}><k.i className="w-4 h-4" /></div>
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-bold text-slate-900 leading-tight truncate">{r.title}</p>
        <p className="text-[10.5px] text-slate-500 leading-tight truncate">{formatDateBR(r.date)}{r.subtitle ? ` • ${r.subtitle}` : ""}</p>
        {r.rkind && r.kind === "saida" && <ReceiptInline kind={r.rkind} id={r.rid} path={r.receipt_path} onDone={onChanged} testid={`conta-dia-receipt-${i}`} />}
      </div>
      <div className="text-right shrink-0">
        <p className={`text-[13px] font-black tabular-nums ${r.amount >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{r.amount >= 0 ? "+ " : "− "}{BRL(Math.abs(r.amount))}</p>
        <p className="text-[10.5px] text-slate-500 tabular-nums">Saldo {BRL(r.balance)}</p>
      </div>
      {r.id && <button onClick={() => onDelete(r)} className="text-slate-300 hover:text-rose-600 mt-1" title="Excluir" data-testid={`conta-dia-del-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>}
    </li>
  );
};

export default function FinanceContaDia() {
  const [data, setData] = useState(null);
  const [mode, setMode] = useState(null);
  const [kind, setKind] = useState("");
  const load = () => api.get("/finance/conta-dia").then((r) => setData(r.data)).catch(() => toast.error("Erro ao carregar"));
  useEffect(() => { load(); }, []);
  const items = useMemo(() => (data?.items || []).filter((r) => !kind || r.kind === kind), [data, kind]);
  const del = async (r) => {
    if (!window.confirm("Excluir este lançamento?")) return;
    await api.delete(`/finance/conta-dia/movs/${r.id}`).then(load).catch(() => toast.error("Erro ao excluir"));
  };
  if (!data) return <div className="py-16 flex justify-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  const started = !!data.start_at;
  return (
    <div className="space-y-5 max-w-3xl pb-32" data-testid="conta-dia-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-slate-900 flex items-center justify-center"><Landmark className="w-5 h-5 text-white" /></div>
        <div className="flex-1 min-w-0">
          <h1 className="font-heading text-xl sm:text-2xl font-black text-slate-900">Conta do dia a dia</h1>
          <p className="text-xs text-slate-500">Recebe as notas, paga DAS, cartão, sócios e gastos. O que não é do Caixa.</p>
        </div>
      </div>
      <section className="rounded-2xl p-5 bg-slate-900 text-white" data-testid="conta-dia-balance-card">
        <p className="text-[10px] font-black uppercase tracking-widest opacity-70">Saldo atual</p>
        <p className="mt-1 text-3xl font-black tabular-nums" data-testid="conta-dia-balance">{BRL(data.balance)}</p>
        <p className="text-[11px] opacity-70 mt-1">{started ? `Lançamentos desde ${formatDateBR(data.start_at.slice(0, 10))}` : "Comece informando o saldo real do banco."}</p>
        <div className="flex gap-2 mt-4 flex-wrap">
          <Button onClick={() => setMode("ajuste")} className="h-9 bg-white text-slate-900 hover:bg-slate-100 font-black" data-testid="conta-dia-btn-ajuste"><Scale className="w-4 h-4 mr-1" /> {started ? "Ajuste de saldo" : "Informar saldo inicial"}</Button>
          {started && <>
            <Button onClick={() => setMode("entrada")} className="h-9 bg-emerald-500 hover:bg-emerald-600 text-white font-black" data-testid="conta-dia-btn-entrada"><Plus className="w-4 h-4 mr-1" /> Entrada</Button>
            <Button onClick={() => setMode("saida")} className="h-9 bg-rose-500 hover:bg-rose-600 text-white font-black" data-testid="conta-dia-btn-saida"><Minus className="w-4 h-4 mr-1" /> Saída</Button>
            <Button onClick={() => setMode("transfer")} className="h-9 bg-sky-500 hover:bg-sky-600 text-white font-black" data-testid="conta-dia-btn-transfer"><ArrowDownLeft className="w-4 h-4 mr-1" /> Trazer do Caixa</Button>
          </>}
        </div>
      </section>
      <p className="text-[11px] text-slate-500 leading-snug">
        Entra automaticamente: ao dar entrada da nota no Caixa, o recebido (nota − ISS) menos o que foi para o Caixa.
        Sai automaticamente: pagamentos dos sócios, DAS e fatura do cartão (quando não marcados &quot;pelo caixa&quot;), gastos pagos em dinheiro/PIX, fechamento da folha/diárias e vales dos funcionários.
      </p>
      {started && <CaixaCdiCard account="conta_dia" label="saldo da conta do dia a dia" onChanged={load} />}
      <section className="bg-white rounded-2xl border border-slate-200 p-5 card-shadow" data-testid="conta-dia-extrato">
        <div className="flex gap-1.5 overflow-x-auto pb-2">
          {FILTERS.map(([k, l]) => (
            <button key={k} onClick={() => setKind(k)} data-testid={`conta-dia-filter-${k || "all"}`}
              className={`h-8 px-3 rounded-full text-xs font-bold whitespace-nowrap transition-colors ${kind === k ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>{l}</button>
          ))}
        </div>
        {items.length === 0 ? <p className="text-sm text-slate-500 italic py-6 text-center">Nenhum movimento.</p>
          : <ul className="divide-y divide-slate-100">{items.map((r, i) => <Row key={`${r.ts}-${i}`} r={r} i={i} onDelete={del} onChanged={load} />)}</ul>}
      </section>
      <MovDialog mode={mode} onClose={() => setMode(null)} balance={data.balance} onSaved={() => { setMode(null); load(); }} />
    </div>
  );
}
